// @ts-strict-ignore
import { BaseArticleInsertProcessor } from './base-article-insert.processor';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { ReplaceReferenceArticleLocationVO } from '@article/model/vo/replace-reference-article-location.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleUtils } from '@article/util/article-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { LabelingArticleConversionDataManager } from '../../../manager/labeling-article-conversion-data.manager';
import { Line } from '@3d-scene/model/line';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { UserModel } from '@user/model/user.model';
import { Injectable } from '@angular/core';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectHelper } from '@project/helper/project.helper';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { RotateArticlePresenter } from '../../../presenter/rotate-article.presenter';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { LineComponentUtils } from '@3d-scene/model3d/util/line-component-utils';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { AddArticleError } from '../../add-article-error';

@Injectable({ providedIn: 'root' })
export class CoverLabelStripArticleInsertProcessor extends BaseArticleInsertProcessor {
  public static readonly MIN_ARTICLE_LENGTH: number = 0.1;
  private static readonly MAX_COVER_LABEL_STRIP_AMOUNT: number = 1;

  public static coverLabelStripInsertionAvailable(insertArticleHelper: InsertArticleHelper, articleAmount: number): boolean {
    return insertArticleHelper.getAllConnectedArticles().length + articleAmount <= CoverLabelStripArticleInsertProcessor.MAX_COVER_LABEL_STRIP_AMOUNT;
  }

  constructor(
    userModel: UserModel,
    globalContainer: GlobalContainer,
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    projectHelper: ProjectHelper,
    localeResourceManager: LocaleResourceManager,
    rotateArticlePresenter: RotateArticlePresenter,
    containerService: ContainerService,
    lineComponentUtils: LineComponentUtils,
    connectionInfoService: ConnectionInfoService,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    articleContainerUtils: ArticleContainerUtils,
    detachArticleUsageService: DetachArticleUsageService,
    private dataManager: LabelingArticleConversionDataManager,
  ) {
    super(
      userModel,
      globalContainer,
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      projectHelper,
      localeResourceManager,
      rotateArticlePresenter,
      containerService,
      lineComponentUtils,
      connectionInfoService,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      articleContainerUtils,
      detachArticleUsageService,
    );
  }

  public findGivingCubeByConnectionArea(containers: ArticleContainer[], baseContainer: ArticleContainer,
    connectionArea: ConnectionAreaVO): Connection {
    return baseContainer.findFirstConnectionByName(connectionArea.name);
  }

  public createArticleContainersWrapper(article: ArticleVO, articleLocation: ArticleLocationVO, insertArticleHelper: InsertArticleHelper): ArticleContainersWrapper {
    let replacedArticleLength: number;
    if (articleLocation instanceof ReplaceReferenceArticleLocationVO) {
      const usageToReplace: ArticleUsageVO = this.projectModel.getArticleUsage(articleLocation.articleUsageIdToReplace);
      if (usageToReplace) {
        replacedArticleLength = PropertyUtils.getNumberPropertyValue(usageToReplace.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH);
      }
    } else {
      if (!CoverLabelStripArticleInsertProcessor.coverLabelStripInsertionAvailable(insertArticleHelper, 1)) {
        throw new AddArticleError(this.localeResourceManager.getValueByKey('articleLocationProcessor.connect.message.error_cover_label_strip_limit'));
      }
    }
    const articleContainersWrapper: ArticleContainersWrapper = super.createArticleContainersWrapper(article, articleLocation, insertArticleHelper);
    const existingUsage: ArticleUsageVO = !articleContainersWrapper.articleUsageID
      ? null
      : this.projectModel.getArticleUsage(articleContainersWrapper.articleUsageID);
    if (
      !existingUsage ||
      !PropertyUtils.containsProperty(existingUsage.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH) ||
      !PropertyUtils.containsProperty(existingUsage.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET)
    ) {
      const articleLength: number = replacedArticleLength || this.getCoverLabelStripArticleLength(article, insertArticleHelper);
      articleContainersWrapper.properties.setValue(ArticleContainerPropertyDef.STRETCH_LENGTH, articleLength);

      if (NumberUtils.isNotNumber(articleLocation.articleOffset)) {
        articleLocation.articleOffset = 0;
      }

      articleContainersWrapper.properties.setValue(ArticleContainerPropertyDef.RELATIVE_OFFSET, articleLocation.articleOffset);
    }
    return articleContainersWrapper;
  }

  public process(
    containers: ArticleContainer[],
    baseContainers: ArticleContainer[],
    articleLine: Line,
    acceptingConnectionCube: Connection,
    givingConnectionCubes: Connection[],
    rotationAngle: number,
    excludedUsageIds: string[] = null,
    isMessageDispatchRequired: boolean = true
  ): void {
    super.process(
      containers,
      baseContainers,
      articleLine,
      acceptingConnectionCube,
      givingConnectionCubes,
      rotationAngle,
      excludedUsageIds,
      isMessageDispatchRequired
    );
    if (this.dataManager.isConverting) {
      containers.forEach((c, index) => {
        if (excludedUsageIds && excludedUsageIds[index]) {
          this.dataManager.applyDataToContainerByUsageId(c, excludedUsageIds[index]);
        }
      });
    }
  }

  private getCoverLabelStripArticleLength(article: ArticleVO, insertArticleHelper: InsertArticleHelper): number {
    const emptySpaceLength: number = insertArticleHelper.baseLine.length;
    if (CoverLabelStripArticleInsertProcessor.MIN_ARTICLE_LENGTH > emptySpaceLength) {
      throw new AddArticleError(this.localeResourceManager.getValueByKey('articleLocationProcessor.connect.message.error_not_enough_free_space'));
    }
    const maxDynamicArticleLength: number = ArticleUtils.getArticleBehaviorPropertyNumberValue(article, ArticleBehaviorPropertyDef.MAX_LENGTH);
    let result: number = emptySpaceLength;
    if (emptySpaceLength > maxDynamicArticleLength) {
      result = maxDynamicArticleLength;
    }
    return result;
  }
}
